import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import AvatarRoundImage from './AvatarRoundImage';

function avatarInitial(name) {
  const txt = String(name || '').trim();
  return txt ? txt[0].toUpperCase() : '?';
}

export default function AvatarOnboardingModal({ open }) {
  const {
    user,
    isAuthResolved,
    avatarCandidates,
    avatarCandidatesLoading,
    loadAvatarCandidates,
    selectAvatar,
  } = useAuth();
  const [selectedId, setSelectedId] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    if (!open || !user || !isAuthResolved) return;
    let cancelled = false;
    (async () => {
      setLoadError('');
      const items = await loadAvatarCandidates();
      if (!cancelled) {
        setSelectedId((prev) => (prev ? prev : (items[0]?.avatar_id || '')));
      }
    })().catch(() => {
      if (!cancelled) setLoadError('Avatar listesi yüklenemedi. Tekrar dene.');
    });
    return () => {
      cancelled = true;
    };
  }, [open, user, isAuthResolved, loadAvatarCandidates]);

  const grouped = useMemo(() => {
    const female = [];
    const male = [];
    const neutral = [];
    for (const a of avatarCandidates || []) {
      const g = String(a?.starter_group || 'neutral').toLowerCase();
      if (g === 'female') female.push(a);
      else if (g === 'male') male.push(a);
      else neutral.push(a);
    }
    return { female, male, neutral };
  }, [avatarCandidates]);

  const onConfirm = async () => {
    if (!selectedId || saving) return;
    setSaving(true);
    setSaveError('');
    try {
      await selectAvatar(selectedId);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Avatar kaydedilemedi. Tekrar dene.';
      setSaveError(typeof msg === 'string' ? msg : 'Avatar kaydedilemedi. Tekrar dene.');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const renderGroup = (title, items) => {
    if (!items.length) return null;
    return (
      <section className="space-y-2">
        <h3 className="text-xs uppercase tracking-[0.18em] text-zinc-500 font-semibold">{title}</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
          {items.map((a) => {
            const active = selectedId === a.avatar_id;
            return (
              <button
                key={a.avatar_id}
                type="button"
                onClick={() => setSelectedId(a.avatar_id)}
                className={`rounded-xl border p-2.5 text-left transition-colors ${
                  active
                    ? 'border-violet-400/60 bg-violet-900/25 ring-1 ring-violet-400/30'
                    : 'border-white/10 bg-black/20 hover:border-white/20'
                }`}
              >
                <div className="flex items-center gap-2.5">
                  {a.image_url ? (
                    <AvatarRoundImage src={a.image_url} cropMeta={a.crop_meta} sizeClass="w-10 h-10" />
                  ) : (
                    <div className="w-10 h-10 rounded-full border border-white/15 bg-zinc-900 text-zinc-200 flex items-center justify-center font-bold">
                      {avatarInitial(a.name)}
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="text-sm text-white font-semibold truncate">{a.name || 'Avatar'}</p>
                    <p className="text-[10px] text-zinc-500">Başlangıç</p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </section>
    );
  };

  return (
    <div
      className="fixed inset-0 z-[120] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="avatar-onboarding-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="avatar-onboarding-title"
    >
      <div className="w-full max-w-2xl rounded-2xl border border-violet-500/25 bg-[#0c0c0c] shadow-[0_20px_70px_-20px_rgba(0,0,0,0.9)] p-4 sm:p-5 space-y-4">
        <div>
          <h2
            id="avatar-onboarding-title"
            className="text-xl font-black text-white"
            style={{ fontFamily: 'Cinzel, serif' }}
          >
            Avatarını seç
          </h2>
          <p className="text-sm text-zinc-400 mt-1">
            Devam etmek için bir başlangıç avatarı seçmen gerekiyor.
          </p>
        </div>

        {avatarCandidatesLoading ? (
          <div className="space-y-2 animate-pulse">
            <div className="h-20 rounded-xl bg-zinc-900/70 border border-white/10" />
            <div className="h-20 rounded-xl bg-zinc-900/70 border border-white/10" />
          </div>
        ) : (
          <div className="space-y-4 max-h-[52vh] overflow-y-auto pr-1">
            {loadError ? (
              <div className="space-y-2">
                <p className="text-sm text-red-300">{loadError}</p>
                <button
                  type="button"
                  onClick={() => {
                    setLoadError('');
                    void loadAvatarCandidates();
                  }}
                  className="text-xs text-violet-300 hover:text-violet-200 underline underline-offset-2"
                >
                  Tekrar yükle
                </button>
              </div>
            ) : (
              <>
                {renderGroup('Kadın', grouped.female)}
                {renderGroup('Erkek', grouped.male)}
                {renderGroup('Diğer', grouped.neutral)}
              </>
            )}
          </div>
        )}

        {saveError ? (
          <p className="text-sm text-red-300 text-center" role="alert">
            {saveError}
          </p>
        ) : null}

        <button
          type="button"
          onClick={onConfirm}
          disabled={!selectedId || avatarCandidatesLoading || saving}
          className="w-full min-h-[2.75rem] rounded-xl bg-violet-600 hover:bg-violet-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold transition-colors"
          data-testid="avatar-onboarding-confirm"
        >
          {saving ? 'Kaydediliyor...' : 'Devam et'}
        </button>
      </div>
    </div>
  );
}
